import { drill } from '../../lib/objectTools';

export const getEdgesFromViewer = (viewer: any, type: string): any[] => {
  const edges = drill(viewer, [type, 'edges']);
  return edges?.filter((edge: any) => edge?.node !== null && edge?.node !== undefined) || [];
};

export const hasNextPage = (viewer: any, type: string): boolean => {
  return drill(viewer, [type, 'pageInfo', 'hasNextPage']) || false;
};

export const getLastCursor = (edges: any[]): string | null => {
  if (edges && edges.length > 0) {
    return edges[edges.length - 1].cursor;
  }
  return null;
};

export const mergeEdges = (previousResult: any, fetchMoreResult: any, type: string): any => {
  const path = ['viewer', type, 'edges'];
  const e1 = drill(previousResult, path);
  const e2 = drill(fetchMoreResult, path);

  if (e1 && e2) {
    const combined = JSON.parse(JSON.stringify(fetchMoreResult));
    combined.viewer[type].edges = e1.concat(e2);
    return combined;
  }
  return previousResult;
};

export const hasNoBookings = (viewer: any, viewerNotFound: boolean): boolean => {
  if (viewerNotFound) {
    return true;
  }
  const requestCount: number = viewer?.requests?.totalCount || 0;
  const upcomingContractsCount: number = viewer?.upcomingContracts?.totalCount || 0;
  return requestCount === 0 && upcomingContractsCount === 0;
};

export const filterCompletedContracts = (contracts: any[], accountKind: string): any[] => {
  if (accountKind === 'PARENT') {
    return contracts.filter((contract) => {
      if (contract.node?.kind === 'booking') {
        const status = contract.node?.scCareSession?.careGiverValidationStatus;
        return ['success', 'pending'].includes(status);
      }
      return contract.node?.kind === 'interview';
    });
  }

  return contracts.filter((contract) => {
    if (contract.node?.kind === 'booking') {
      const status = contract.node?.scCareSession?.careGiverValidationStatus;
      return ['success'].includes(status);
    }
    return contract.node?.kind === 'interview';
  });
};

export const getCompletedContracts = (viewer: any, accountKind: string): any[] => {
  const allContracts = getEdgesFromViewer(viewer, 'completedContracts');
  return filterCompletedContracts(allContracts, accountKind);
};

export const hasCompletedContracts = (contracts: any[]): boolean => {
  return contracts.length > 0;
};

export const createLoadMoreHandler = (apolloQueries: any, viewerQuery: string, edges: any[], type: string) => {
  const lastCursor = getLastCursor(edges);
  if (!lastCursor) return;

  apolloQueries[viewerQuery].fetchMore({
    variables: { after: lastCursor },
    updateQuery: (previousResult: any, { fetchMoreResult }: any) => {
      return mergeEdges(previousResult, fetchMoreResult, type);
    },
  });
};

export const fetchBookingsQuery = async (
  apollo: any,
  query: any,
  variables: Record<string, any>,
  onSuccess: (viewer: any) => void,
  onError: (error: any) => void,
  setLoading: (isLoading: boolean) => void
): Promise<void> => {
  setLoading(true);
  try {
    const { data } = await apollo.query({
      query,
      variables,
      errorPolicy: 'all',
    });
    onSuccess(data.viewer);
  } catch (error) {
    onError(error);
  } finally {
    setLoading(false);
  }
};

export const loadMoreBookingsQuery = async (
  apollo: any,
  query: any,
  variables: Record<string, any>,
  viewer: any,
  edges: any[],
  type: string,
  onSuccess: (mergedViewer: any) => void,
  onError: (error: any) => void,
  setLoading: (loading: boolean) => void
): Promise<void> => {
  const lastCursor = getLastCursor(edges);
  if (!lastCursor) {
    return;
  }

  setLoading(true);
  try {
    const { data } = await apollo.query({
      query,
      variables: { ...variables, after: lastCursor },
      errorPolicy: 'all',
    });
    const mergedResult = mergeEdges({ viewer }, { viewer: data.viewer }, type);
    onSuccess(mergedResult.viewer);
  } catch (error) {
    onError(error);
  } finally {
    setLoading(false);
  }
};

export const processApolloError = (error: any, queryType: string = 'bookings'): void => {
  if (error.graphQLErrors) {
    for (let i = 0; i < error.graphQLErrors.length; i += 1) {
      const e = error.graphQLErrors[i];
      if (e.extensions?.error_code !== 'not_found') {
        console.error(`Unexpected Apollo error in ${queryType} query`, e);
      }
    }
  }
};
